import { createApi } from '@reduxjs/toolkit/query/react';
import { collectionApiInstance } from '@/shared/api/axios';
import { CollectionMode, DatasetType, SkuVariant } from '@/shared/api/types';
import { axiosBaseQuery } from './base-query';

export const collectionApi = createApi({
  reducerPath: 'collectionApi',
  baseQuery: axiosBaseQuery(collectionApiInstance),
  tagTypes: ['Variant'],
  endpoints: (builder) => ({
    collections: builder.query<CollectionResponse, CollectionVariables>({
      query: ({ ...params }) => ({
        url: `sku_in_box/collections`,
        method: 'GET',
        params,
      }),
    }),
    collectionImages: builder.query<ImagesResponse, ImagesVariables>({
      query: ({ collection_id, ...params }) => ({
        url: `sku_in_box/collections/${collection_id}/images`,
        method: 'GET',
        params,
      }),
    }),
    variantCollection: builder.query<
      VariantCollectionResponse,
      { sku: string }
    >({
      providesTags: ['Variant'],
      query: ({ sku, ...params }) => ({
        url: `sku_in_box/collections/variant/${sku}`,
        method: 'GET',
        params,
      }),
    }),
    image: builder.query<
      { result: FrameImageDetail },
      { collection_id: string; image_id: string }
    >({
      query: ({ collection_id, image_id }) => ({
        url: `sku_in_box/collections/${collection_id}/images/${image_id}`,
        method: 'GET',
      }),
    }),
    createCollection: builder.mutation<
      CreateCollectionResponse,
      CreateCollectionVariables
    >({
      query: (variables) => ({
        method: 'POST',
        url: `sku_in_box/collections`,
        data: variables,
      }),
    }),
    captureImage: builder.mutation<CaptureImageResponse, CaptureImageVariables>(
      {
        query: ({ zone_id, collection_id }) => ({
          method: 'POST',
          url: `sku_in_box/collections/${collection_id}/capture`,
          data: { zone_id },
        }),
      }
    ),
    createImage: builder.mutation<CreateImageResponse, CreateImageVariables>({
      query: ({ collection_id, ...params }) => ({
        method: 'POST',
        url: `sku_in_box/collections/${collection_id}/images`,
        data: params,
      }),
    }),
    deleteImage: builder.mutation<
      void,
      { collection_id: string; image_id: string }
    >({
      query: ({ collection_id, image_id }) => ({
        method: 'DELETE',
        url: `sku_in_box/collections/${collection_id}/images/${image_id}`,
      }),
    }),
    complete: builder.mutation<CompleteResponse, CompleteVariables>({
      query: ({ collection_id }) => ({
        method: 'POST',
        url: `sku_in_box/collections/${collection_id}/complete`,
      }),
      invalidatesTags: ['Variant'],
    }),

    /**
     * Not used in the app
     */
    variantsSync: builder.mutation<VariantsResponse, void>({
      query: () => ({
        method: 'POST',
        url: `variants/sync`,
      }),
    }),

    variants: builder.query<VariantsResponse, VariantsVariables>({
      query: ({ page, limit, search, ordering, sku }) => ({
        method: 'GET',
        url: `variants`,
        params: {
          page,
          limit,
          search,
          ordering,
          sku,
        },
      }),
    }),

    /**
     * Not used in the app
     */
    statistics: builder.query<VariantStatsResponse, VariantsVariables>({
      query: ({ page, limit, search, ordering }) => ({
        method: 'GET',
        url: `variants`,
        params: {
          page,
          limit,
          search,
          ordering,
        },
      }),
    }),
  }),
});

export const {
  useCollectionsQuery,
  useCreateCollectionMutation,
  useCaptureImageMutation,
  useCollectionImagesQuery,
  useCompleteMutation,
  useCreateImageMutation,
  useLazyCollectionImagesQuery,
  useLazyImageQuery,
  useVariantsQuery,
  useLazyVariantsQuery,
  useDeleteImageMutation,
  useVariantCollectionQuery,
} = collectionApi;

export type Collection = {
  id: string;
  name: string;
  status: string;
  created_by: string;
  cloud_upload_state: number;
  segment_state: number;
  dataset_type: DatasetType;
  mode: CollectionMode;
  zone_id: string;
};

type CollectionResponse = {
  results: Collection[];
  count: number;
};

type CollectionVariables = {
  page: number;
  limit: number;
  search?: string;
  ordering?: string;
  is_owner?: boolean;
};

export type FrameImageDetail = {
  id: string;
  image_id: string;
  image_url: string;
  background_image_id: string;
  background_image_url: string;
  skus: string[];
  variants: SkuVariant[];
  points: [number, number][];
  polygons: [number, number][][];
  polygon_finder_parameters: {
    top_left: [number, number];
    bottom_right: [number, number];
  };
  capture_date: string;
  cvat_task_id: number;
  cvat_job_id: number;
  cvat_job_stage: string;
};

export type FrameImage = Omit<
  FrameImageDetail,
  'image_url' | 'background_image_url'
>;

export type ImagesResponse = {
  results: FrameImage[];
  count: number;
};
type ImagesVariables = {
  collection_id: string;
  page: number;
  limit: number;
  search?: string;
  ordering?: string;
};

type CreateCollectionResponse = {
  result: {
    id: string;
    name: string;
    status: string;
    created_by: string;
    dataset_type: string;
    mode: string;
  };
};

type CreateCollectionVariables = {
  name: string;
  dataset_type: string;
  mode: string;
  zone_id: string;
};

export type CaptureImageResponse = {
  result: {
    png_image_path: string;
    webp_image_path: string;
    width: number;
    height: number;
  };
};
type CaptureImageVariables = {
  collection_id: string;
  zone_id: string;
};

type CompleteResponse = {
  result: {
    id: string;
    name: string;
    status: string;
    created_by: string;
    dataset_type: string;
    mode: string;
  };
};

type CompleteVariables = {
  collection_id: string;
};

export type CreateImageResponse = {
  result: {
    id: string;
    image_id: string;
    background_image_id: string;
    skus: string[];
    points: [number, number][];
    capture_date: string;
    polygons: [number, number][][];
    polygon_finder_parameters: Record<string, any>;
    cvat_task_id: number;
    cvat_job_id: number;
    cvat_job_stage: string;
  };
};
export type CreateImageVariables = {
  collection_id: string;
  image_id: string;
  background_image_id: string;
  variant_ids: number[];
  points: [number, number][];
  capture_date: string;
  polygon_finder_parameters: Record<string, any>;
};

type VariantsResponse = {
  results: SkuVariant[];
  count: number;
};

type VariantsVariables = {
  page: number;
  limit: number;
  search?: string;
  ordering?: string;
  sku?: string;
};

type VariantStatsResponse = {
  results: {
    id: number;
    sku: string;
    rms_id: string;
    display_name: string;
    image: string;
    sku_in_box_training: number;
    sku_in_box_validating: number;
  }[];
  count: 0;
};

type VariantCollectionResponse = {
  results: VariantCollecton[];
  count: number;
};

export type VariantCollecton = {
  id: string;
  name: string;
  dataset_type: DatasetType;
  sample_image_url: string;
  created_by: string;
  created_at: string;
  total_image_count: number;
  containing_image_count: number;
};
